import { useEffect, useRef, useState } from 'react';

export type Suggestion = {
  key: string;
  title: string;
  rank?: string;
  /** Страна — у подсказок месторождений: выбор строки заполняет и её. */
  country?: string | null;
};

/**
 * Поле с подсказками по мере ввода.
 *
 * Свободный ввод разрешён всегда, даже когда справочник подключён:
 * настаивать на выборе из списка нельзя. Внешний источник в критическом
 * пути ввода однажды приведёт к тому, что образец просто не занесут —
 * потому что Mindat не отвечает, а «зелёного камня из отвала» в нём и
 * так нет (ТЗ 8.4).
 */
export function SuggestField({
  value,
  onChange,
  fetchSuggestions,
  placeholder,
  autoFocus,
  onPick,
}: {
  value: string;
  onChange: (value: string) => void;
  fetchSuggestions: (query: string) => Promise<Suggestion[]>;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Выбранная строка целиком, а не только её текст. Нужно там, где за
   * подсказкой стоит запись справочника: одного названия мало, чтобы
   * потом найти именно её.
   */
  onPick?: (item: Suggestion) => void;
}) {
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value.trim()) {
      setItems([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchSuggestions(value)
        .then((r) => {
          if (cancelled) return;
          // Точное совпадение НЕ выбрасывается, хотя раньше выбрасывалось:
          // «подсказывать нечего» было верно, пока подсказка только
          // дописывала текст. Со справочником строка несёт смысл — формулу,
          // пометку «вид из справочника», подтверждение, что это тот самый
          // минерал. Без неё набранное целиком «Кальцит» показывало одни
          // разновидности, и выглядело это как «вид не найден».
          setItems(r);
        })
        .catch(() => setItems([]));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, fetchSuggestions]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  return (
    <div ref={box} className="suggest-wrap">
      <input
        className="field-input"
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && items.length > 0 && (
        <div className="suggest">
          {items.map((item) => (
            <button
              type="button"
              key={item.key}
              onClick={() => {
                onChange(item.title);
                onPick?.(item);
                setOpen(false);
              }}
            >
              <div className="sp">{item.title}</div>
              {item.rank && <div className="rank">{item.rank}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
